import { useEffect, useState } from 'react';
import { api, type Asset, type TalentRow } from '../../lib/api';
import { sayQuote } from '../../lib/quick';
import { sayWhat } from '../../lib/models';
import { PICTURE_WAIT, sayWait, useElapsed } from '../../lib/waiting';
import { Thumb, displayName } from '../AssetGrid';
import { ModelPicker } from '../ModelPicker';
import { ModelOptions } from '../ModelOptions';
import { OpenInPro, QuoteLine, SendButton, Stage, WatchNote, useAssetUrl, useWatchAsset } from './parts';
import { useBench } from './useBench';
import type { TabProps } from './Quick';

// The Image tab: pick a model, describe the picture, press, see it.
//
// The same call the Assets bench makes, with the two things that bench
// never said: the price before the press and the picture after it. Who is
// in it comes from the cast — their portraits go as references, so a
// character keeps their face — and "match a picture" takes up to three
// library pictures the way the Pro bench does. The model and its size come
// from the catalogue (204); the credits come from the server's quote, never
// from a number here.

const MAX_PEOPLE = 3;
const MAX_REFS = 3;

export function QuickImage({ brand, models, pass, onUsedPass, onOpenInPro, prefill, onUsedPrefill, onError }: TabProps) {
  const [prompt, setPrompt] = useState('');
  const [cast, setCast] = useState<string[]>([]);
  const [refs, setRefs] = useState<string[]>([]);
  const [talent, setTalent] = useState<TalentRow[]>([]);
  const [pictures, setPictures] = useState<Asset[]>([]);
  const [madeId, setMadeId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const waited = useElapsed(startedAt);
  const brandId = brand?.id ?? null;
  const { model, setModel, sel, setSel, quote, credits } = useBench('image', models.image);

  useEffect(() => {
    let live = true;
    api.talent().then((r) => { if (live) setTalent(r.talent); }).catch(() => undefined);
    api.assets(brandId).then((all) => { if (live) setPictures(all.filter((a) => a.kind === 'image' && a.status === 'ready').slice(0, 12)); }).catch(() => undefined);
    return () => { live = false; };
  }, [brandId]);

  // A proposal fills the prompt once and is handed back — a starting point, never a command.
  useEffect(() => {
    if (!prefill || prefill.action !== 'make_picture') return;
    const p = String(prefill.args.prompt ?? '');
    if (p) setPrompt(p);
    onUsedPrefill?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  // A picture handed from the Library becomes a reference, once.
  useEffect(() => {
    if (!pass || pass.tab !== 'image') return;
    setRefs((r) => (r.includes(pass.assetId) || r.length >= MAX_REFS ? r : [...r, pass.assetId]));
    onUsedPass();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pass]);

  const faceless = cast.map((id) => talent.find((t) => t.id === id)).filter((t): t is TalentRow => Boolean(t) && t!.portrait !== 'ready');
  const why = !model ? 'Choose a model'
    : prompt.trim().length < 5 ? 'Describe the picture'
    : faceless.length ? `${faceless.map((t) => t.name).join(', ')} ${faceless.length === 1 ? 'has' : 'have'} no portrait yet`
    : quote && !quote.balance.ok ? `Short by ${quote.balance.shortBy.toLocaleString()} credits`
    : null;

  const watch = useWatchAsset(madeId, brandId);
  const url = useAssetUrl(watch.asset);
  useEffect(() => { if (!busy && startedAt !== null && watch.asset?.status === 'ready') setStartedAt(null); }, [busy, startedAt, watch.asset?.status]);

  async function send() {
    if (!model) return;
    setBusy(true); setStartedAt(Date.now()); setMadeId(null); onError(null);
    try {
      const a = await api.generateAsset(prompt.trim(), brandId, {
        model: model.id,
        ...(sel.resolution ? { resolution: sel.resolution } : {}), ...(sel.quality ? { quality: sel.quality } : {}),
        ...(cast.length ? { talent: cast } : {}),
        ...(refs.length ? { references: refs } : {}),
      });
      setMadeId(a.id);
    } catch (e) { onError((e as Error).message); setStartedAt(null); } finally { setBusy(false); }
  }

  const what = sayWhat('image', model, sel);
  return (
    <div className="quick-tab">
      <Stage empty="Your picture lands here — about twenty seconds to draw, then a minute while the render plane looks at it.">
        {url ? <img src={url} alt={prompt} /> : null}
      </Stage>
      <WatchNote asset={watch.asset} stalled={watch.stalled} lookAgain={watch.lookAgain} waiting={sayWait(busy, waited)} />

      <ModelPicker bench="image" models={models.image ?? null} value={model} onChange={setModel} />
      <ModelOptions bench="image" model={model} value={sel} onChange={setSel} />

      <textarea className="quick-prompt" value={prompt} rows={3} maxLength={model?.constraints.promptChars ? Math.min(2000, model.constraints.promptChars) : 500} placeholder="A sunlit kitchen with a wooden island, wide shot, morning light" onChange={(e) => setPrompt(e.target.value)} />

      {talent.length > 0 && (
        <div className="quick-chips" data-tool="quick-cast">
          <span className="sub-dim">Who is in it</span>
          {talent.map((t) => (
            <button key={t.id} type="button" className={`chip${cast.includes(t.id) ? ' on' : ''}`}
                    disabled={!cast.includes(t.id) && cast.length >= MAX_PEOPLE}
                    title={t.portrait === 'ready' ? 'Their portrait goes with the prompt, so they keep their face' : 'No portrait yet — draw one on Talent first'}
                    onClick={() => setCast((c) => (c.includes(t.id) ? c.filter((x) => x !== t.id) : [...c, t.id]))}>
              {t.name}
            </button>
          ))}
        </div>
      )}

      {pictures.length > 0 && (
        <div className="quick-chips" data-tool="quick-refs">
          <span className="sub-dim">Match a picture</span>
          <div className="quick-strip">
            {pictures.map((a) => (
              <button key={a.id} type="button" className={`quick-strip-item${refs.includes(a.id) ? ' on' : ''}`} title={displayName(a)}
                      disabled={!refs.includes(a.id) && refs.length >= MAX_REFS}
                      onClick={() => setRefs((r) => (r.includes(a.id) ? r.filter((x) => x !== a.id) : [...r, a.id]))}>
                <Thumb asset={a} size={96} />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="quick-actions">
        <SendButton label="Make it" busyLabel="Drawing… " busy={busy} credits={credits} why={why} onClick={() => void send()} />
        <OpenInPro what="The Assets bench: the library, uploads, and this prompt with it." onClick={() => onOpenInPro({
          bench: 'assets', move: 'assets-make',
          prefill: { action: 'make_picture', say: '', args: { prompt: prompt.trim() }, bench: 'assets', move: 'assets-make', inline: false },
        })} />
      </div>
      <QuoteLine text={credits === null ? null : `${sayQuote(credits, what, quote?.balance)} ${PICTURE_WAIT}.`} />
    </div>
  );
}
